// utilities/reportSettings.js
// Pure helpers for the Wallet Reports tab (v2): the "Scheduled & Email"
// settings card, its recipients chip input, the audit toast after a save, and
// the compact money format the Overview / Payments tiles use.
// No React, no network. Everything here is covered by reportSettings.test.js.
//
// The settings shape mirrors the wallet's WalletSetting key `reports.schedule`:
//   { dailyEnabled, dailyHourEAT (0-23), monthlyEnabled, autoReconcile,
//     recipients:[email], attachments:{ summaryCsv, registerCsv, ledgerCsv },
//     sendWhenEmpty, updatedBy?, updatedAt? }

export const MAX_RECIPIENTS = 20;

// Simple RFC-ish check, the same class of regex the wallet validates with:
// one @, no whitespace, a dot in the domain, a 2+ letter TLD.
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

export const REPORT_SETTINGS_DEFAULTS = Object.freeze({
  dailyEnabled:   true,
  dailyHourEAT:   1,
  monthlyEnabled: true,
  autoReconcile:  true,
  recipients:     [],
  attachments:    Object.freeze({ summaryCsv: true, registerCsv: true, ledgerCsv: false }),
  sendWhenEmpty:  false,
});

export const ATTACHMENT_KEYS = ["summaryCsv", "registerCsv", "ledgerCsv"];
export const ATTACHMENT_LABELS = {
  summaryCsv:  "Summary CSV",
  registerCsv: "Register CSV",
  ledgerCsv:   "Ledger CSV",
};

export const SETTING_LABELS = {
  dailyEnabled:            "Daily report",
  dailyHourEAT:            "Daily hour",
  monthlyEnabled:          "Monthly report",
  autoReconcile:           "Auto-reconcile",
  recipients:              "Recipients",
  "attachments.summaryCsv":  "Summary CSV attachment",
  "attachments.registerCsv": "Register CSV attachment",
  "attachments.ledgerCsv":   "Ledger CSV attachment",
  sendWhenEmpty:           "Send when empty",
};

export const isValidEmail = (s) => {
  if (typeof s !== "string") return false;
  const v = s.trim();
  return v.length > 0 && v.length <= 254 && EMAIL_RE.test(v);
};

const cleanEmail = (s) => String(s || "").trim().toLowerCase();

/**
 * Split free text into recipients. Accepts commas, semicolons, whitespace
 * and newlines as separators; lower-cases and de-duplicates.
 * "a@b.com, B@C.io; x" -> { valid:["a@b.com","b@c.io"], invalid:["x"] }
 */
export function parseRecipients(text) {
  const valid = [];
  const invalid = [];
  const seen = new Set();
  String(text || "")
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .forEach((tok) => {
      const e = cleanEmail(tok);
      if (isValidEmail(e)) {
        if (!seen.has(e)) { seen.add(e); valid.push(e); }
      } else if (!invalid.includes(tok)) {
        invalid.push(tok);
      }
    });
  return { valid, invalid };
}

/**
 * Chip-input merge: current list + typed text -> the next list.
 * Returns { recipients, added, invalid, overflow } where overflow lists the
 * valid addresses that did not fit under MAX_RECIPIENTS.
 */
export function addRecipients(current, text, max = MAX_RECIPIENTS) {
  const base = uniqueEmails(current);
  const { valid, invalid } = parseRecipients(text);
  const added = [];
  const overflow = [];
  valid.forEach((e) => {
    if (base.includes(e) || added.includes(e)) return;
    if (base.length + added.length >= max) overflow.push(e);
    else added.push(e);
  });
  return { recipients: [...base, ...added], added, invalid, overflow };
}

export function removeRecipient(current, email) {
  const e = cleanEmail(email);
  return uniqueEmails(current).filter((x) => x !== e);
}

export const uniqueEmails = (list) => {
  const out = [];
  (Array.isArray(list) ? list : []).forEach((x) => {
    const e = cleanEmail(x);
    if (e && isValidEmail(e) && !out.includes(e)) out.push(e);
  });
  return out;
};

/** 24 options for the daily-hour select, labelled as EAT wall-clock hours. */
export function hourOptionsEAT() {
  return Array.from({ length: 24 }, (_, h) => ({ value: h, label: `${String(h).padStart(2, "0")}:00` }));
}

export const formatHourEAT = (h) => {
  if (h == null || h === "") return "n/a";
  const n = Number(h);
  return Number.isInteger(n) && n >= 0 && n <= 23 ? `${String(n).padStart(2, "0")}:00 EAT` : "n/a";
};

const toBool = (v, fallback) => {
  if (typeof v === "boolean") return v;
  if (v === "true" || v === 1 || v === "1") return true;
  if (v === "false" || v === 0 || v === "0") return false;
  return fallback;
};

/**
 * Whatever the wallet (or a half-filled form) hands us -> a complete, typed
 * settings object the form can bind to. Unknown keys are dropped; updatedBy /
 * updatedAt are kept when present so the card can show "Last saved by".
 */
export function toSettingsForm(input) {
  const src = input && typeof input === "object" ? input : {};
  const d = REPORT_SETTINGS_DEFAULTS;
  const hour = src.dailyHourEAT == null || src.dailyHourEAT === "" ? NaN : Number(src.dailyHourEAT);
  const att = src.attachments && typeof src.attachments === "object" ? src.attachments : {};
  const out = {
    dailyEnabled:   toBool(src.dailyEnabled, d.dailyEnabled),
    dailyHourEAT:   Number.isInteger(hour) && hour >= 0 && hour <= 23 ? hour : d.dailyHourEAT,
    monthlyEnabled: toBool(src.monthlyEnabled, d.monthlyEnabled),
    autoReconcile:  toBool(src.autoReconcile, d.autoReconcile),
    recipients:     uniqueEmails(src.recipients).slice(0, MAX_RECIPIENTS),
    attachments: {
      summaryCsv:  toBool(att.summaryCsv, d.attachments.summaryCsv),
      registerCsv: toBool(att.registerCsv, d.attachments.registerCsv),
      ledgerCsv:   toBool(att.ledgerCsv, d.attachments.ledgerCsv),
    },
    sendWhenEmpty:  toBool(src.sendWhenEmpty, d.sendWhenEmpty),
  };
  if (src.updatedBy) out.updatedBy = String(src.updatedBy);
  if (src.updatedAt) out.updatedAt = String(src.updatedAt);
  return out;
}

/** The PUT body: the form minus the server-owned audit fields. */
export function settingsPayload(form) {
  const f = toSettingsForm(form);
  delete f.updatedBy;
  delete f.updatedAt;
  return f;
}

/**
 * What changed between two settings objects, for the audit toast.
 * Scalars -> { key, before, after }; recipients -> { key:"recipients", added, removed }.
 * Attachments are flattened to "attachments.<name>". Empty array = no change.
 */
export function diffSettings(before, after) {
  const a = toSettingsForm(before);
  const b = toSettingsForm(after);
  const changes = [];
  ["dailyEnabled", "dailyHourEAT", "monthlyEnabled", "autoReconcile", "sendWhenEmpty"].forEach((k) => {
    if (a[k] !== b[k]) changes.push({ key: k, before: a[k], after: b[k] });
  });
  ATTACHMENT_KEYS.forEach((k) => {
    if (a.attachments[k] !== b.attachments[k]) changes.push({ key: `attachments.${k}`, before: a.attachments[k], after: b.attachments[k] });
  });
  const added = b.recipients.filter((e) => !a.recipients.includes(e));
  const removed = a.recipients.filter((e) => !b.recipients.includes(e));
  if (added.length || removed.length) changes.push({ key: "recipients", added, removed });
  return changes;
}

const fmtValue = (key, v) => {
  if (key === "dailyHourEAT") return formatHourEAT(v);
  if (typeof v === "boolean") return v ? "on" : "off";
  return String(v);
};

/** "Daily hour 01:00 EAT → 02:00 EAT · Recipients +a@b.com −c@d.com" */
export function formatSettingsDiff(changes) {
  const list = Array.isArray(changes) ? changes : [];
  if (!list.length) return "No changes";
  return list
    .map((c) => {
      const label = SETTING_LABELS[c.key] || c.key;
      if (c.key === "recipients") {
        const parts = [
          ...(c.added || []).map((e) => `+${e}`),
          ...(c.removed || []).map((e) => `-${e}`),
        ];
        return `${label} ${parts.join(" ")}`;
      }
      return `${label} ${fmtValue(c.key, c.before)} → ${fmtValue(c.key, c.after)}`;
    })
    .join(" · ");
}

/**
 * Compact money for tiles: UGX 950 · UGX 12.5K · UGX 1.2M · UGX 3.4B.
 * One decimal, trailing ".0" dropped; null / NaN -> "—"; negatives keep the sign.
 */
export function formatMoneyShort(n, currency = "UGX") {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  const one = (x) => {
    const s = x.toFixed(1);
    return s.endsWith(".0") ? s.slice(0, -2) : s;
  };
  let body;
  if (abs < 1000) body = String(Math.round(abs));
  else if (abs < 1e6) body = `${one(abs / 1e3)}K`;
  else if (abs < 1e9) body = `${one(abs / 1e6)}M`;
  else body = `${one(abs / 1e9)}B`;
  return `${sign}${currency} ${body}`.trim();
}

/** successCount / count as a 0-1 fraction, null when there is nothing to divide. */
export function successRateOf(w) {
  const count = Number(w?.count);
  const ok = Number(w?.successCount);
  if (!Number.isFinite(count) || count <= 0 || !Number.isFinite(ok)) return null;
  return Math.max(0, Math.min(1, ok / count));
}

const reportSettings = {
  MAX_RECIPIENTS, EMAIL_RE, REPORT_SETTINGS_DEFAULTS, ATTACHMENT_KEYS, ATTACHMENT_LABELS, SETTING_LABELS,
  isValidEmail, parseRecipients, addRecipients, removeRecipient, uniqueEmails, hourOptionsEAT, formatHourEAT,
  toSettingsForm, settingsPayload, diffSettings, formatSettingsDiff, formatMoneyShort, successRateOf,
};
export default reportSettings;
